import { useState, useRef, useCallback, MouseEvent } from "react";
import {
  Box,
  Typography,
  Popover,
  Popper,
  Paper,
  Stack,
  IconButton,
  ClickAwayListener,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import { TrendingDown, TrendingUp, Close } from "@material-ui/icons";
import { numberToCurrency } from "utils/listings/display";
import { fDate } from "utils/formatTime";
import { fNumber } from "utils/formatNumber";
import { useTranslation } from "react-i18next";
import useSettings from "hooks/useSettings";
import { PriceHistoryEntry } from "../../../@types/sharedSchema";

function getPriceChangeInfo(price: number, priceHistory?: PriceHistoryEntry[]) {
  const previousPrice = priceHistory?.length
    ? [...priceHistory].reverse().find((entry) => entry.price !== price && entry.price > 0)?.price
    : undefined;
  const hasPriceChange = previousPrice !== undefined;
  const priceChangePercent = hasPriceChange
    ? Math.ceil(Math.abs(((price - previousPrice) / previousPrice) * 100))
    : 0;
  const isPriceIncrease = hasPriceChange && price > previousPrice;
  return { hasPriceChange, previousPrice, priceChangePercent, isPriceIncrease };
}

interface ListingPriceWithHistoryProps {
  price: number;
  priceHistory?: PriceHistoryEntry[];
  variant?: "body1" | "subtitle1";
  onClickCapture?: (e: MouseEvent) => void;
}

function PriceHistoryContent({
  priceHistory,
  isRTL,
  onClose
}: {
  priceHistory: PriceHistoryEntry[];
  isRTL: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Stack sx={{ p: 2, minWidth: 200, maxWidth: 320 }} gap={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">{t("listingsPage.priceHistory")}</Typography>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>
      {priceHistory.map((entry, index) => (
        <Stack
          key={index}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: index === priceHistory.length - 1 ? "bold" : "normal",
              textDecoration: index !== priceHistory.length - 1 ? "line-through" : "none",
              color: index !== priceHistory.length - 1 ? "text.secondary" : "text.primary"
            }}
          >
            {`₪${fNumber(entry.price)}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fDate(entry.date, isRTL)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

export function ListingPriceWithHistory({
  price,
  priceHistory,
  variant = "body1",
  onClickCapture
}: ListingPriceWithHistoryProps) {
  const { themeDirection } = useSettings();
  const isRTL = themeDirection === "rtl";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>();

  const { hasPriceChange, priceChangePercent, isPriceIncrease } = getPriceChangeInfo(
    price,
    priceHistory
  );

  const close = useCallback(() => setAnchorEl(null), []);

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onClickCapture?.(e);
    setAnchorEl(e.currentTarget);
  };

  const handleMouseEnter = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (isMobile) return;
      clearTimeout(hoverTimeout.current);
      setAnchorEl(e.currentTarget);
    },
    [isMobile]
  );

  const handleMouseLeave = useCallback(() => {
    if (isMobile) return;
    hoverTimeout.current = setTimeout(() => setAnchorEl(null), 150);
  }, [isMobile]);

  const cancelClose = useCallback(() => clearTimeout(hoverTimeout.current), []);

  if (!hasPriceChange) {
    return (
      <Typography variant={variant} style={{ fontWeight: "bold" }}>
        {numberToCurrency(price)}
      </Typography>
    );
  }

  const priceBox = (
    <Box
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 1,
        border: "1px solid",
        borderColor: isPriceIncrease ? "warning.light" : "info.light",
        bgcolor: isPriceIncrease ? "warning.lighter" : "info.lighter",
        cursor: "pointer",
        "&:hover": { bgcolor: isPriceIncrease ? "warning.light" : "info.light", opacity: 0.8 }
      }}
    >
      {isPriceIncrease ? (
        <TrendingUp sx={{ fontSize: 16 }} color="warning" />
      ) : (
        <TrendingDown sx={{ fontSize: 16 }} color="info" />
      )}
      <Typography variant={variant} style={{ fontWeight: "bold" }}>
        {numberToCurrency(price)}
      </Typography>
      <Typography
        variant="caption"
        fontWeight="bold"
        color={isPriceIncrease ? "warning.dark" : "info.main"}
      >
        {isPriceIncrease ? "+" : "-"}
        {priceChangePercent}%
      </Typography>
    </Box>
  );

  if (isMobile) {
    return (
      <>
        {priceBox}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          onClick={(e) => e.stopPropagation()}
        >
          <PriceHistoryContent priceHistory={priceHistory!} isRTL={isRTL} onClose={close} />
        </Popover>
      </>
    );
  }

  return (
    <>
      {priceBox}
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom"
        style={{ zIndex: theme.zIndex.tooltip }}
      >
        <ClickAwayListener onClickAway={close}>
          <Paper
            elevation={8}
            onMouseEnter={cancelClose}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => e.stopPropagation()}
          >
            <PriceHistoryContent priceHistory={priceHistory!} isRTL={isRTL} onClose={close} />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
